import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import styles from './style.module.scss'
import BtnToggle from '../../../../UI/BtnToggle'
import MiniHeader from '../../../../UI/MiniHeader'
import Search from '../../../../UI/Search'
import Table from '../../../../UI/Table'
import { TApi } from '../../../../shared/const'
import { getFormattedDate } from '../../../../shared/helpers'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useLatestOnly from '../../../../shared/useLatestOnly'
import http from '../../../../shared/http'
import { useTranslation } from 'react-i18next'
import BtnTooltip from '../../../../UI/BtnTooltip'
import eventEmitter, { types } from '../../../../shared/eventEmitter'
import { connect } from 'react-redux'

import Avatar from 'react-avatar'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import { ceoField, parentalConfirmationField } from '../../../../UI/FilterPanel/fields'
import BtnFilter from '../../../../UI/BtnFilter'
import StatusIcon from '../../../../UI/StatusIcon'
import Pagination from '../../../../UI/Pagination'
import { logIn, setTeacherCompanyView } from '../../../../redux/ducks/user'
import { useAvatarUrls } from '../../../../shared/useAvatarUrls'

/*
 * Vier Staende je Nachweis: geprueft, abgegeben, beanstandet, offen.
 *
 * Bis 22.08.2026 standen dafuer zwei PNG-Grafiken in fester Farbe und fester
 * Aufloesung (`warning.png`, `checked.png`) — Emoji-Bilder, die nirgends sonst
 * in der Anwendung vorkommen. Jetzt ist es `UI/StatusIcon`: Strich-SVGs in
 * `currentColor`, dieselben Zeichen wie in den Listen der Verwaltung.
 *
 * Die Bedingungen standen zweimal fast gleich im Seiteninhalt (einmal fuer die
 * Elternbestaetigung, einmal fuer den Kursnachweis) und liefen dabei schon
 * auseinander: „Offen" war bei der Elternbestaetigung gruen und beim
 * Kursnachweis orange.
 */
const NACHWEIS_STAENDE = {
	checked: { icon: 'check', label: 'Checked', className: 'statusChecked' },
	submitted: { icon: 'check', label: 'Submitted', className: 'statusSubmitted' },
	open: { icon: 'warning', label: 'Open', className: 'statusOpen' },
	rejected: { icon: 'warning', label: 'Rejected', className: 'statusRejected' }
}

const nachweisStand = ({ vorhanden, reviewed, rejected }) => {
	if (vorhanden && rejected) return NACHWEIS_STAENDE.rejected
	if (vorhanden && reviewed) return NACHWEIS_STAENDE.checked
	if (vorhanden) return NACHWEIS_STAENDE.submitted

	return NACHWEIS_STAENDE.open
}

function NachweisStatus({ vorhanden, reviewed, rejected, t }) {
	const stand = nachweisStand({ vorhanden, reviewed, rejected })

	return (
		<span className={clsx(styles.statusValue, styles[stand.className])}>
			<StatusIcon name={stand.icon} size={18} />
			{t(stand.label)}
		</span>
	)
}

function StudentsList(props) {
	const { t } = useTranslation()
	const [students, setStudents] = useState([])
	const [countAll, setCountAll] = useState(0)
	const [assigned, setAssigned] = useState(true)
	const [page, setPage] = useState(0)
	const [sort, setSort] = useState({ name: 'firstName', direction: 'asc' })
	const [search, setSearch] = useState('')
	const limit = 10
	const [showFilter, setShowFilter] = useState(false)

	const fields = useMemo(() => [parentalConfirmationField(t), ceoField(t)], [t])

	const filters = useFilters({ storageKey: 'students-teacher', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)
	const latest = useLatestOnly()

	// Bilder werden angemeldet geholt (siehe shared/useAvatarUrls) — ein
	// <img src> könnte den Anmelde-Kopfeintrag nicht mitschicken.
	const avatarUrls = useAvatarUrls(students.map(item => item.avatarHashName))

	const fetchStudents = () => {
		const isCurrent = latest()

		http.get(assigned ? TApi.TEACHER_GET_ASSIGNED_STUDENTS : TApi.TEACHER_GET_STUDENTS, {
			params: {
				limit,
				offset: page * limit,
				order: sort.name,
				direction: sort.direction,
				search: debouncedSearch,
				filter: filterQuery
			}
		}).then(({ data }) => {
			if (!isCurrent()) return

			setStudents(assigned ? data.allAssignedStudents.rows : data.allStudents.rows)
			setCountAll(assigned ? data.allAssignedStudents.count : data.allStudents.count)
		})
	}

	// Gefiltert und gesucht wird ohne Knopfdruck; jede Aenderung setzt die Seite
	// zurueck, sonst stuende man auf Seite 4 einer kuerzer gewordenen Liste.
	useEffect(() => {
		fetchStudents()

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, assigned, sort, debouncedSearch, filterQuery])

	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const handleSearch = e => {
		setPage(0)
		setSearch(e.target.value)
	}

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}

	const handleStudentMode = async studentId => {
		const {
			data: { token }
		} = await http.get(TApi.TEACHER_GET_TOKEN_STUDENT(studentId))

		http.defaults.headers['Authorization'] = `Bearer ${token}`
		props.setTeacherCompanyView(true)
		const { data } = await http.get(TApi.COMMON_ME, { disableToast: true })
		props.logIn({
			role: data.role,
			profile: data
		})
	}

	/*
	 * Dieselben Handlungen im Drei-Punkte-Menue und im geoeffneten Profil: Wer
	 * ein Profil offen hat, soll zum Handeln nicht in die Liste zurueckgehen
	 * muessen. Das Fenster nimmt sie als vierten Parameter entgegen.
	 */
	const studentActions = student => [
		{
			label: t('Switch to student view'),
			onClick: closeModal => {
				if (closeModal) closeModal()
				handleStudentMode(student.id)
			}
		}
	]

	const openProfile = student =>
		eventEmitter.emit(types.openStudentProfile, student, fetchStudents, studentActions(student))

	const columns = useMemo(
		() => [
			{
				Header: () => {
					return <span className={styles.tableTitleName}>Name</span>
				},
				minWidth: 250,
				accessor: 'firstName',
				Cell: ({ row }) => {
					return (
						<div className={styles.companyNameContainer}>
							<div>
								<Avatar
									className={styles.userAvatar}
									round
									size={32}
									name={`${row.original.firstName} ${row.original.lastName}`}
									src={avatarUrls[row.original.avatarHashName] || ''}
								/>

								<span className={styles.name}>
									{row.original.firstName} {row.original.lastName}
								</span>
							</div>
							<BtnTooltip
								items={[
									{
										label: t('Switch to student view'),
										onClick: () => handleStudentMode(row.original.id)
									},
									{
										label: t('View profile'),
										onClick: () => openProfile(row.original)
									}
								]}
							/>
						</div>
					)
				}
			},
			{
				Header: t('Company'),
				accessor: 'companyId',
				Cell: ({ row }) => {
					return (
						<span className={styles.company}>
							{row.original.EmploymentCompany ? row.original.EmploymentCompany.name : 'null'}
						</span>
					)
				}
			},
			{
				Header: t('Work Area'),
				accessor: 'primaryWorkArea',
				Cell: ({ row }) => {
					return (
						<p className={styles.position}>
							{row.original.primaryWorkArea
								? t(row.original.primaryWorkArea)
								: t('No main work area yet')}
						</p>
					)
				}
			},
			{
				Header: t('Registration Date'),
				accessor: 'createdAt',
				Cell: ({ row }) => {
					return <span className={styles.registrationDate}>{getFormattedDate(row.original.createdAt)}</span>
				}
			},
			{
				Header: t('Parental Confirmation'),
				accessor: 'reviewedParentalConfirmation',
				Cell: ({ row }) => (
					<div className={styles.statusContainer}>
						<NachweisStatus
							t={t}
							vorhanden={!!row.original.Documents[0]}
							reviewed={row.original.reviewedParentalConfirmation}
							rejected={row.original.rejectedParentalConfirmation}
						/>
					</div>
				)
			}
		],
		// avatarUrls gehoert dazu: Die Bilder treffen erst nach dem ersten Rendern
		// ein, ohne die Abhaengigkeit blieben die Spalten auf dem leeren Stand.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[students, avatarUrls, t]
	)
	return (
		<div className={styles.root}>
			<MiniHeader className={styles.headerContainer} title={t('students')}>
				<div className={styles.header}>
					<div className={styles.toggleContainer}>
						<p className={styles.toggleLeftTitle}>{t('Assigned Students')}</p>
						<BtnToggle
							onClick={() => {
								setAssigned(!assigned)
							}}
						/>
						<p className={styles.toggleRightTitle}>{t('All Students In School')}</p>
					</div>
					<div className={styles.searchContainer}>
						<Search
							placeholder={t('Search for a student')}
							onChange={handleSearch}
							value={search}
							onSubmit={e => e.preventDefault()}
						/>
						<BtnFilter
							icon="arrow"
							onClick={() => setShowFilter(true)}
							count={filters.count}
						>
							{t('Filter')} ({filters.count})
						</BtnFilter>
					</div>
				</div>
			</MiniHeader>
			<div className={styles.subHeader}>
				<Pagination limit={limit} page={page} label={t('students')} countAll={countAll} onChange={setPage} />
			</div>
			<div className="container">
				<Table
					columns={columns}
					items={students}
					page={page}
					limit={limit}
					countAll={countAll}
					onChange={setPage}
					handleSort={handleSort}
					onRowClick={openProfile}
					rowTitle={t('View profile')}
				/>
			</div>
			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}

StudentsList.propTypes = {
	setTeacherCompanyView: PropTypes.func.isRequired,
	logIn: PropTypes.func.isRequired
}

const mapStateToProps = state => ({
	workAreas: state.options.workAreas
})
const mapDispatchToProps = dispatch => ({
	setTeacherCompanyView: data => dispatch(setTeacherCompanyView(data)),
	logIn: data => dispatch(logIn(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(StudentsList)
